import type { GridColDef } from "@mui/x-data-grid-premium";
import React from "react";
import type { QuantityUnit } from "../../../../clients/graphqlTypes.js";
import type { EstimatedCalculation__ItemFragment } from "../../doc-items/boxes/EstimatedCalculationBox.generated.js";
import { useQuantityUnits } from "../../doc-items/useQuantityUnits.js";
import type { DocDataGrid_ItemFragment } from "../DocDataGrid.generated.js";
import { DataGridUnitSelectEditCell } from "../cells/DataGridUnitSelectEditCell.js";
import type { ModifyItemCalculationFn } from "../quote/useModifyItemCalculation.js";
import type { DocDataGridRow } from "../types.js";
import { makeCalculationValueSetter } from "../cells/helpers.js";

interface Options<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
> {
  headerName: string;
  canEdit: boolean;
  modifyItemCalculation: ModifyItemCalculationFn;
  itemCanBeEdited?: (item: T) => boolean;
}

function UnitCell({ unit }: { unit: QuantityUnit | undefined }) {
  const { quantityUnitLabels } = useQuantityUnits();
  return <span>{unit ? quantityUnitLabels[unit] : ""}</span>;
}

export function createQuantityUnitColumn<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
>(options: Options<T>): GridColDef<DocDataGridRow<T>> {
  return {
    field: "quantityUnit",
    headerName: options.headerName,
    width: 90,
    editable: options.canEdit,
    sortable: false,
    filterable: false,
    valueGetter: (_value, row) =>
      row.item?.proposedCalculation?.quantityUnit ?? undefined,
    valueSetter: makeCalculationValueSetter<T>()(
      "quantityUnit",
      (itemId, value) =>
        options.modifyItemCalculation(itemId, {
          quantityUnit: value,
        })
    ),
    renderCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.type !== "paid") return null;
      if (options.itemCanBeEdited?.(item) === false) return null;
      return <UnitCell unit={params.value as QuantityUnit | undefined} />;
    },
    renderEditCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.type !== "paid") return null;
      if (options.itemCanBeEdited?.(item) === false) return null;
      return <DataGridUnitSelectEditCell {...params} />;
    },
  };
}
